import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  IconButton,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import DeleteConfirmDialog from './DeleteConfirmDialog';
import {
  ACCEPTED_DOCUMENT_TYPES,
  documentDisplayName,
  getEntityDocumentUrl,
  isStoredDocument,
  removeEntityDocument,
  uploadEntityDocument,
  validateDocumentFile
} from '../../services/entityDocumentsService';

const DocumentCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const UploadButton = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.paper,
  // Markenfarbe nur für Rahmen/Icon: Gelb als Schrift auf Weiß ist kaum lesbar.
  color: theme.palette.text.primary,
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
  },
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  width: '100%',
  height: '56px',
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('de-DE');
};

/**
 * Dokumentenliste mit Upload nach Firebase Storage (Fahrzeug bzw. Fahrer).
 *
 * Mit `entityId` wird sofort hochgeladen und der Eintrag direkt am Datensatz
 * gespeichert — unabhängig vom „Speichern" des Formulars. Ohne `entityId`
 * (Neuanlage) werden die Dateien nur vorgemerkt (`pendingFiles`); das Formular
 * lädt sie nach dem Anlegen hoch, weil der Storage-Pfad die ID braucht.
 * `readOnly` zeigt nur die Liste mit „Anzeigen" (Detailansichten).
 */
const EntityDocumentsSection = ({
  kind,
  entityId,
  documents,
  onDocumentsChange,
  pendingFiles = [],
  onPendingFilesChange,
  inputId = 'document-upload',
  readOnly = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [openingIndex, setOpeningIndex] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const entries = Array.isArray(documents) ? documents : [];

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    // Input zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
    e.target.value = '';
    if (files.length === 0) return;

    setError('');
    const invalid = files.map(validateDocumentFile).filter(Boolean);
    const valid = files.filter(file => !validateDocumentFile(file));

    if (!entityId) {
      onPendingFilesChange?.([...pendingFiles, ...valid]);
      if (invalid.length) setError(invalid.join(' '));
      return;
    }

    const errors = [...invalid];
    let current = entries;
    setUploading(true);
    try {
      for (const file of valid) {
        setProgress(0);
        try {
          const entry = await uploadEntityDocument(kind, entityId, file, setProgress);
          current = [...current, entry];
          onDocumentsChange(current);
        } catch (uploadError) {
          console.error('Dokument-Upload fehlgeschlagen:', uploadError);
          errors.push(`„${file.name}" konnte nicht hochgeladen werden.`);
        }
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
    if (errors.length) setError(errors.join(' '));
  };

  const handleOpen = async (entry, index) => {
    setError('');
    setOpeningIndex(index);
    try {
      const url = await getEntityDocumentUrl(entry);
      window.open(url, '_blank', 'noopener');
    } catch (openError) {
      console.error('Dokument konnte nicht geöffnet werden:', openError);
      setError('Das Dokument konnte nicht geöffnet werden.');
    } finally {
      setOpeningIndex(null);
    }
  };

  const handleConfirmDelete = async () => {
    const entry = pendingDelete;
    if (!entry) return;
    setDeleting(true);
    setError('');
    try {
      await removeEntityDocument(kind, entityId, entry);
      onDocumentsChange(entries.filter(item => item !== entry));
    } catch (deleteError) {
      console.error('Dokument konnte nicht gelöscht werden:', deleteError);
      setError('Das Dokument konnte nicht gelöscht werden.');
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleRemovePending = (index) => {
    onPendingFilesChange?.(pendingFiles.filter((_, i) => i !== index));
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        {entries.map((entry, index) => {
          const stored = isStoredDocument(entry);
          const meta = stored
            ? [formatSize(entry.size), formatDate(entry.uploadDate)].filter(Boolean).join(' · ')
            : '';
          return (
            <DocumentCard key={stored ? entry.storagePath : `legacy-${index}`} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
                  {documentDisplayName(entry)}
                </Typography>
                {meta && (
                  <Typography variant="body2" color="text.secondary">
                    {meta}
                  </Typography>
                )}
                {!stored && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Keine Datei gespeichert — bitte neu hochladen"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                {stored && (
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => handleOpen(entry, index)}
                    disabled={openingIndex === index}
                  >
                    Anzeigen
                  </Button>
                )}
                {entityId && !readOnly && (
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Dokument löschen"
                    onClick={() => setPendingDelete(entry)}
                    disabled={uploading || deleting}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </CardActions>
            </DocumentCard>
          );
        })}

        {pendingFiles.map((file, index) => (
          <DocumentCard key={`pending-${index}-${file.name}`} variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatSize(file.size)} · wird nach dem Anlegen hochgeladen
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <IconButton
                size="small"
                color="error"
                aria-label="Vorgemerktes Dokument entfernen"
                onClick={() => handleRemovePending(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </CardActions>
          </DocumentCard>
        ))}

        {entries.length === 0 && pendingFiles.length === 0 && (
          <Alert severity="info">
            {readOnly ? 'Keine Dokumente hinterlegt.' : 'Keine Dokumente hochgeladen.'}
          </Alert>
        )}
      </Box>

      {!readOnly && (
      <>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Dokument wird hochgeladen … {Math.round(progress)} %
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      <UploadButton
        htmlFor={inputId}
        sx={uploading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
      >
        <UploadIcon sx={{ mr: 1 }} />
        Dokument hochladen
      </UploadButton>
      <HiddenInput
        id={inputId}
        type="file"
        accept={ACCEPTED_DOCUMENT_TYPES}
        onChange={handleFilesSelected}
        disabled={uploading}
        multiple
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        PDF oder Bild, maximal 20 MB je Datei.
        {entityId ? ' Änderungen an Dokumenten werden sofort gespeichert.' : ''}
      </Typography>
      </>
      )}

      <DeleteConfirmDialog
        open={!!pendingDelete}
        title="Dokument löschen"
        content={`„${documentDisplayName(pendingDelete)}" wird endgültig gelöscht.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => !deleting && setPendingDelete(null)}
      />
    </>
  );
};

export default EntityDocumentsSection;
